import { Metadata } from '@redwoodjs/web';
import { RiBriefcaseLine, RiFileList3Line, RiGroupLine, RiMapPinLine, RiPriceTag3Line } from '@remixicon/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExportGridButton from 'src/components/atoms/ExportGridButton';
import Flex from 'src/components/atoms/Flex';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import { AgTab, AgTabs } from 'src/components/ui/AgTabs';
import BusinessEntitiesGrid from 'src/modules/settings/business/businessEntity/BusinessEntitiesGrid';
import type { AppRouterContext } from 'src/routing/routerAccess';
import { useSettingsBusinessSearch } from 'src/routing/routeSearch';
import { preloadBusinessEntitiesGrid } from './businessEntity/hooks/useBusinessEntities';
import CustomerGroups from './customerGroups/CustomerGroups';
import { preloadCustomerGroupsGrid } from './customerGroups/hooks/useCustomerGroups';
import ExpenseTypesGrid from './expenseTypes/ExpenseTypesGrid';
import { preloadExpenseTypesGrid } from './expenseTypes/hooks/useExpenseTypes';
import MyAddressesGrid from './myAddresses/MyAddressesGrid';
import { preloadMyAddressesGrid } from './myAddresses/myAddresses.api';
import PricingSheets from './pricingSheets/containers/PricingSheets';
import { preloadPricingSheetsGrid } from './pricingSheets/hooks/usePricingSheetsPage';

export enum BusinessSettingsTabs {
  COMMODITIES = 'commodities',
  BUSINESS_ENTITIES = 'business-entities',
  MY_ADDRESSES = 'my-addresses',
  CHARGE_TYPES = 'charge-types',
  CUSTOMER_GROUPS = 'customer-groups',
  PRICING_SHEETS = 'pricing-sheets',
}

/** The shown tab's query: each tab's grid reads its loader ref. */
export function preloadBusinessSetupTab(context: AppRouterContext, tab: string | undefined) {
  switch (tab ?? BusinessSettingsTabs.BUSINESS_ENTITIES) {
    case BusinessSettingsTabs.BUSINESS_ENTITIES:
      return { businessEntitiesRef: preloadBusinessEntitiesGrid(context) };
    case BusinessSettingsTabs.MY_ADDRESSES:
      return { organizationPlacesRef: preloadMyAddressesGrid(context) };
    case BusinessSettingsTabs.CHARGE_TYPES:
      return { expenseTypesRef: preloadExpenseTypesGrid(context) };
    case BusinessSettingsTabs.CUSTOMER_GROUPS:
      return { customerGroupsRef: preloadCustomerGroupsGrid(context) };
    case BusinessSettingsTabs.PRICING_SHEETS:
      return { pricingSheetsRef: preloadPricingSheetsGrid(context) };
    default:
      return {};
  }
}

export default function BusinessSettingsPage() {
  const { pricingSheets } = useFlags();
  const { tab } = useSettingsBusinessSearch();

  const isBusinessEntity = tab === BusinessSettingsTabs.MY_ADDRESSES;

  return (
    <>
      <Metadata title="Business Setup" />
      <PageHeader pageType={PageType.BusinessSettings}>
        <Flex center sx={{ gap: 1, position: 'relative', width: '100%' }}>
          <div className="flex flex-grow" />
          <ExportGridButton canPrint={!isBusinessEntity} />
        </Flex>
      </PageHeader>
      <PageBody>
        <AgTabs queryParam="tab">
          <AgTab
            id={BusinessSettingsTabs.BUSINESS_ENTITIES}
            label="Business Entities"
            icon={<RiBriefcaseLine size={20} />}
          >
            <BusinessEntitiesGrid />
          </AgTab>
          <AgTab id={BusinessSettingsTabs.MY_ADDRESSES} label="My Addresses" icon={<RiMapPinLine size={20} />}>
            <MyAddressesGrid />
          </AgTab>
          <AgTab id={BusinessSettingsTabs.CHARGE_TYPES} label="Charge Types" icon={<RiFileList3Line size={20} />}>
            <ExpenseTypesGrid />
          </AgTab>
          {pricingSheets && (
            <AgTab id={BusinessSettingsTabs.CUSTOMER_GROUPS} label="Customer Groups" icon={<RiGroupLine size={20} />}>
              <CustomerGroups />
            </AgTab>
          )}
          {pricingSheets && (
            <AgTab id={BusinessSettingsTabs.PRICING_SHEETS} label="Pricing Sheets" icon={<RiPriceTag3Line size={20} />}>
              <PricingSheets />
            </AgTab>
          )}
        </AgTabs>
      </PageBody>
    </>
  );
}
